import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IncludedExcludedCounter } from 'components/common/types/IncludedExcludedCounter.types';
import { NavigationItem } from 'components/common/types/NavigationItem.types';
import { POI, PoiOption } from 'components/common/types/Planner.types';
import { Store } from 'components/common/types/Store.types';
import { areArraysEqual } from 'utils/arrayUtils';
import { CUSTOM_POI_DATA_PROVIDER, POI_DEFAULT_RADIUS } from './consts';
import { useCustomPOIUpload } from './CustomPOIUpload';
import { enrichCustomPOIs } from './CustomPOIUpload/customPOIUpload.utils';
import { UsePOI } from './PointsOfInterest.types';
import {
  calculateChildrenCounts,
  consolidatePOIs,
  countDescendants,
  createPOIFromOption,
  createMissingLeafPOIs,
  findOptionByPath,
  findSelectedAncestor,
  getAllLeafDescendants,
  getChildrenUniformState,
  getDescendantsOfItem,
  getItemPath,
  getLeafDescendantsUnderAncestor,
  getLeafSelectionKey,
  getPoiSelectionKey,
  getSiblingOptions,
  hasChildren,
  isChildOfPath,
  isLeafChildOfParent,
  isSameOption,
  matchesParent,
  removeParent,
} from './usePOI.utils';

interface UsePOIParams {
  pois: POI[];
  selectedProvider: string;
  onConfirm: (selectedPOI: POI[]) => void;
  numberOfAllowedPOIs?: number;
  navigationStack: NavigationItem[];
  customPoiMaxRows?: number;
}

export const usePOI = ({
  pois,
  selectedProvider,
  onConfirm,
  numberOfAllowedPOIs,
  navigationStack,
  customPoiMaxRows,
}: UsePOIParams): UsePOI => {
  const [selectedPOIs, setSelectedPOIs] = useState(pois);
  const [isNumberOfPOIsExceeded, setIsNumberOfPOIsExceeded] = useState(false);
  const [currentFocusedItemIndex, setCurrentFocusedItemIndex] = useState<number | null>(null);

  const addCustomSelectedPOIs = useCallback((options: PoiOption[], provider: string): void => {
    setSelectedPOIs((prev) => {
      const existingPaths = new Set(prev.map((poi) => poi.categoryPath));
      const newPOIs = options.reduce<POI[]>((acc, option) => {
        if (!existingPaths.has(getItemPath(option))) {
          acc.push(createPOIFromOption(option, option.include ?? true, option.radius ?? POI_DEFAULT_RADIUS, provider));
        }
        return acc;
      }, []);

      return newPOIs.length > 0 ? [...prev, ...newPOIs] : prev;
    });
  }, []);

  const removeSelectedPOIsByCategory = useCallback((categoryPath: string): void => {
    setSelectedPOIs((prev) =>
      prev.filter((poi) => poi.categoryPath !== categoryPath && !poi.categoryPath.startsWith(`${categoryPath}/`)),
    );
  }, []);

  const handleCategoryCreated = useCallback(
    (category: PoiOption) => {
      addCustomSelectedPOIs(category.children ?? [], CUSTOM_POI_DATA_PROVIDER);
    },
    [addCustomSelectedPOIs],
  );

  const handleCategoryDeleted = useCallback(
    (category: PoiOption) => {
      const categoryPath = category.categoryPath || category.categoryType || '';
      removeSelectedPOIsByCategory(categoryPath);
    },
    [removeSelectedPOIsByCategory],
  );

  const {
    customPoiCategories,
    customPoiFiles,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isCustomCategory,
  } = useCustomPOIUpload({
    onCategoryCreated: handleCategoryCreated,
    onCategoryDeleted: handleCategoryDeleted,
    initialPois: pois,
    customPoiMaxRows,
  });

  const enrichedOnConfirm = useCallback(
    (confirmedPois: POI[]): void => {
      if (customPoiFiles.length === 0) {
        onConfirm(confirmedPois);
        return;
      }
      onConfirm(enrichCustomPOIs(confirmedPois, customPoiFiles));
    },
    [onConfirm, customPoiFiles],
  );

  const dataProvidersWithPoiOptions = useSelector((state: Store) => state.userOptions.dataProvidersWithPoiOptions);
  const poiOptions = useMemo(
    () => dataProvidersWithPoiOptions[selectedProvider] ?? [],
    [dataProvidersWithPoiOptions, selectedProvider],
  );

  const combinedPoiOptions = useMemo(() => [...poiOptions, ...customPoiCategories], [poiOptions, customPoiCategories]);

  const isSelectedPOIChanged = useMemo(() => {
    const getPOIFingerprint = ({
      categoryPath,
      include,
      radius,
    }: POI): Pick<POI, 'categoryPath' | 'include' | 'radius'> => ({
      categoryPath,
      include,
      radius,
    });

    return (
      pois.length !== selectedPOIs.length ||
      !areArraysEqual(pois.map(getPOIFingerprint), selectedPOIs.map(getPOIFingerprint))
    );
  }, [pois, selectedPOIs]);

  const isAnyRadiusOfPOIZero = useMemo(() => selectedPOIs.some((poi) => poi.radius === 0), [selectedPOIs]);

  const getSelectedPOI = useCallback(
    (item: PoiOption): POI | undefined =>
      selectedPOIs.find(
        (poi) => poi.code === item.categoryType && poi.name === item.name && poi.hasChildren === hasChildren(item),
      ),
    [selectedPOIs],
  );

  const getSelectedParentPOI = useCallback(
    (item: PoiOption): POI | undefined => findSelectedAncestor(item, selectedPOIs),
    [selectedPOIs],
  );

  const isItemSelected: UsePOI['isItemSelected'] = useCallback(
    (item) => {
      if (hasChildren(item) && getDescendantsOfItem(item, selectedPOIs).length > 0) {
        return getChildrenUniformState(item.children ?? [], selectedPOIs).isSameState;
      }

      return getSelectedPOI(item) !== undefined || getSelectedParentPOI(item) !== undefined;
    },
    [selectedPOIs, getSelectedParentPOI, getSelectedPOI],
  );

  const getSelectedPOIIncludeAndRadius: UsePOI['getSelectedPOIIncludeAndRadius'] = useCallback(
    (item) => {
      if (hasChildren(item) && getDescendantsOfItem(item, selectedPOIs).length > 0) {
        const { isSameState, isSameIncludedState, firstChild } = getChildrenUniformState(
          item.children ?? [],
          selectedPOIs,
        );

        if (!isSameState && isSameIncludedState && firstChild) {
          return { include: firstChild.include, radius: undefined };
        }

        if (isSameState && firstChild) {
          return { include: firstChild.include, radius: firstChild.radius };
        }

        return { include: undefined, radius: undefined };
      }

      const selectedPOI = getSelectedPOI(item);

      if (selectedPOI) {
        return { include: selectedPOI.include, radius: selectedPOI.radius };
      }

      const parent = getSelectedParentPOI(item);

      if (parent) {
        return { include: parent.include, radius: parent.radius };
      }

      return { include: undefined, radius: undefined };
    },
    [selectedPOIs, getSelectedParentPOI, getSelectedPOI],
  );

  const getSelectedPOIIncludedExcludedChildrenCounter = useCallback(
    (item: PoiOption): IncludedExcludedCounter => {
      return hasChildren(item)
        ? calculateChildrenCounts(item.children ?? [], selectedPOIs)
        : countDescendants(getDescendantsOfItem(item, selectedPOIs));
    },
    [selectedPOIs],
  );

  const handleDeselection = useCallback(
    (targetPoi: POI, parent: POI | undefined, siblings: PoiOption[] | undefined, item: PoiOption): void => {
      setSelectedPOIs((prev) => {
        let updated = prev.filter((poi) => !matchesParent(poi, targetPoi));

        if (parent && siblings?.length) {
          const siblingsToAdd = siblings.flatMap((sibling) =>
            isSameOption(sibling, item)
              ? []
              : [createPOIFromOption(sibling, parent.include, parent.radius, parent.dataProvider)],
          );

          updated = [...removeParent(updated, parent), ...siblingsToAdd];
        }

        return updated;
      });
    },
    [],
  );

  const handleIncludeChange = useCallback((targetPoi: POI, isNewInclude: boolean): void => {
    setSelectedPOIs((prev) =>
      prev.map((poi) => {
        if (matchesParent(poi, targetPoi)) return { ...poi, include: isNewInclude };
        if (targetPoi.hasChildren && isChildOfPath(poi, targetPoi.categoryPath))
          return { ...poi, include: isNewInclude };

        return poi;
      }),
    );
  }, []);

  const deselectUnderAncestor = useCallback(
    (ancestor: POI, excludedLeafKeys: Set<string>): void => {
      const ancestorOption = findOptionByPath(combinedPoiOptions, ancestor.categoryPath);

      setSelectedPOIs((prev) => {
        const keptPOIs = removeParent(prev, ancestor).filter((poi) => !excludedLeafKeys.has(getPoiSelectionKey(poi)));

        if (!ancestorOption) return keptPOIs;

        const keptKeys = new Set(keptPOIs.map(getPoiSelectionKey));

        const collectRemaining = (option: PoiOption): POI[] => {
          const leafKeys = getAllLeafDescendants(option).map(getLeafSelectionKey);
          const isFullyExcluded = leafKeys.every((key) => excludedLeafKeys.has(key));

          if (isFullyExcluded) return [];

          const isBranchAffected =
            leafKeys.some((key) => excludedLeafKeys.has(key) || keptKeys.has(key)) ||
            keptKeys.has(getLeafSelectionKey(option));

          if (!isBranchAffected) {
            return [createPOIFromOption(option, ancestor.include, ancestor.radius, ancestor.dataProvider)];
          }

          return (option.children ?? []).flatMap(collectRemaining);
        };

        return [...keptPOIs, ...collectRemaining(ancestorOption)];
      });
    },
    [combinedPoiOptions],
  );

  const handleInheritedIncludeChange = useCallback(
    (parent: POI, siblings: PoiOption[], item: PoiOption): void => {
      if (item.include === undefined) {
        deselectUnderAncestor(parent, new Set(getAllLeafDescendants(item).map(getLeafSelectionKey)));

        return;
      }

      const { include } = item;

      if (item.parentCode !== parent.name) {
        const newPOI = createPOIFromOption(item, include, parent.radius, parent.dataProvider);

        setSelectedPOIs((prev) => [...prev, newPOI]);
        return;
      }

      if (siblings.length === 1) {
        setSelectedPOIs((prev) => prev.map((poi) => (matchesParent(poi, parent) ? { ...poi, include } : poi)));

        return;
      }

      const siblingsToAdd = siblings.map((sibling) =>
        createPOIFromOption(
          sibling,
          isSameOption(sibling, item) ? include : parent.include,
          parent.radius,
          parent.dataProvider,
        ),
      );

      setSelectedPOIs((prev) => [...removeParent(prev, parent), ...siblingsToAdd]);
    },
    [deselectUnderAncestor],
  );

  const handleNewSelection = useCallback((item: PoiOption, provider: string): void => {
    const itemPath = getItemPath(item);

    if (item.include === undefined) {
      setSelectedPOIs((prev) => prev.filter((poi) => poi.categoryPath !== itemPath && !isChildOfPath(poi, itemPath)));

      return;
    }

    const newPOI = createPOIFromOption(item, item.include, item.radius ?? POI_DEFAULT_RADIUS, provider);

    setSelectedPOIs((prev) => {
      const filtered = newPOI.hasChildren ? prev.filter((poi) => !isChildOfPath(poi, newPOI.categoryPath)) : prev;

      return [...filtered, newPOI];
    });
  }, []);

  const handleVisibleLeavesChange = useCallback(
    (item: PoiOption, provider: string, existingParentPOI?: POI, selectedAncestorPOI?: POI): void => {
      const visibleLeaves = getAllLeafDescendants(item);
      const visibleLeafKeys = new Set(visibleLeaves.map(getLeafSelectionKey));
      const itemPath = getItemPath(item);
      const allLeaves = getLeafDescendantsUnderAncestor(combinedPoiOptions, itemPath);
      const { include, radius = POI_DEFAULT_RADIUS } = item;

      if (include === undefined) {
        if (!existingParentPOI && selectedAncestorPOI) {
          deselectUnderAncestor(selectedAncestorPOI, visibleLeafKeys);

          return;
        }

        setSelectedPOIs((prev) => {
          if (!existingParentPOI) {
            return prev.filter((poi) => !visibleLeafKeys.has(getPoiSelectionKey(poi)));
          }

          const nonVisibleLeaves = allLeaves.filter((leaf) => !visibleLeafKeys.has(getLeafSelectionKey(leaf)));
          const withoutParent = removeParent(prev, existingParentPOI);
          const withoutVisible = withoutParent.filter((poi) => !visibleLeafKeys.has(getPoiSelectionKey(poi)));
          const existingKeys = new Set(withoutVisible.map(getPoiSelectionKey));

          return [
            ...withoutVisible,
            ...createMissingLeafPOIs(
              nonVisibleLeaves,
              existingKeys,
              existingParentPOI.include,
              existingParentPOI.radius,
              existingParentPOI.dataProvider,
            ),
          ];
        });

        return;
      }

      setSelectedPOIs((prev) => {
        const baseList = existingParentPOI ? removeParent(prev, existingParentPOI) : prev;
        const existingKeys = new Set(baseList.map(getPoiSelectionKey));
        const previousWithNewInclude = baseList.map((poi) =>
          visibleLeafKeys.has(getPoiSelectionKey(poi)) ? { ...poi, include } : poi,
        );
        const visibleLeavesToAdd = createMissingLeafPOIs(visibleLeaves, existingKeys, include, radius, provider);

        if (!existingParentPOI) {
          return [...previousWithNewInclude, ...visibleLeavesToAdd];
        }

        const nonVisibleLeaves = allLeaves.filter((leaf) => !visibleLeafKeys.has(getLeafSelectionKey(leaf)));

        return [
          ...previousWithNewInclude,
          ...visibleLeavesToAdd,
          ...createMissingLeafPOIs(
            nonVisibleLeaves,
            existingKeys,
            existingParentPOI.include,
            existingParentPOI.radius,
            existingParentPOI.dataProvider,
          ),
        ];
      });
    },
    [combinedPoiOptions, deselectUnderAncestor],
  );

  const handleVisibleLeavesRadiusChange = useCallback(
    (item: PoiOption, radius: number, existingParentPOI?: POI): void => {
      const visibleLeaves = getAllLeafDescendants(item);
      const visibleLeafKeys = new Set(visibleLeaves.map(getLeafSelectionKey));
      const itemPath = getItemPath(item);
      const allLeaves = getLeafDescendantsUnderAncestor(combinedPoiOptions, itemPath);

      setSelectedPOIs((prev) => {
        if (!existingParentPOI) {
          return prev.map((poi) => (visibleLeafKeys.has(getPoiSelectionKey(poi)) ? { ...poi, radius } : poi));
        }

        const baseList = removeParent(prev, existingParentPOI);
        const existingKeys = new Set(baseList.map(getPoiSelectionKey));
        const previousWithNewRadius = baseList.map((poi) =>
          visibleLeafKeys.has(getPoiSelectionKey(poi)) ? { ...poi, radius } : poi,
        );
        const visibleLeavesToAdd = createMissingLeafPOIs(
          visibleLeaves,
          existingKeys,
          existingParentPOI.include,
          radius,
          existingParentPOI.dataProvider,
        );
        const nonVisibleLeaves = allLeaves.filter((leaf) => !visibleLeafKeys.has(getLeafSelectionKey(leaf)));

        return [
          ...previousWithNewRadius,
          ...visibleLeavesToAdd,
          ...createMissingLeafPOIs(
            nonVisibleLeaves,
            existingKeys,
            existingParentPOI.include,
            existingParentPOI.radius,
            existingParentPOI.dataProvider,
          ),
        ];
      });
    },
    [combinedPoiOptions],
  );

  const handleDescendantsRadiusChange = useCallback((item: PoiOption, radius: number): void => {
    const itemPath = getItemPath(item);

    setSelectedPOIs((prev) => prev.map((poi) => (isChildOfPath(poi, itemPath) ? { ...poi, radius } : poi)));
  }, []);

  const onIncludeExcludeChange: UsePOI['onIncludeExcludeChange'] = useCallback(
    (item, itemIndex, provider) => {
      setCurrentFocusedItemIndex((prev) => (prev !== itemIndex && item.include !== undefined ? itemIndex : prev));

      const existingPOI = getSelectedPOI(item);
      const parent = getSelectedParentPOI(item);
      const siblings = getSiblingOptions(combinedPoiOptions, item);
      const itemHasChildren = hasChildren(item);
      const isNarrowedParent =
        itemHasChildren &&
        getAllLeafDescendants(item).length <
          getLeafDescendantsUnderAncestor(combinedPoiOptions, getItemPath(item)).length;
      const isNonRootParent = itemHasChildren && Boolean(item.parentCode);

      if (isNarrowedParent || isNonRootParent) {
        handleVisibleLeavesChange(item, provider, existingPOI, parent);

        return;
      }

      if (existingPOI && item.include === undefined) {
        handleDeselection(existingPOI, parent, siblings, item);

        return;
      }

      if (existingPOI && item.include !== undefined) {
        handleIncludeChange(existingPOI, item.include);

        return;
      }

      if (parent && siblings.length > 0) {
        handleInheritedIncludeChange(parent, siblings, item);

        return;
      }

      handleNewSelection(item, provider);
    },
    [
      getSelectedPOI,
      getSelectedParentPOI,
      handleDeselection,
      handleIncludeChange,
      handleInheritedIncludeChange,
      handleVisibleLeavesChange,
      handleNewSelection,
      combinedPoiOptions,
    ],
  );

  const handleDirectRadiusChange = useCallback((targetPoi: POI, radius: number): void => {
    setSelectedPOIs((prev) => prev.map((poi) => (matchesParent(poi, targetPoi) ? { ...poi, radius } : poi)));
  }, []);

  const handleInheritedRadiusChange = useCallback(
    (parent: POI, siblings: PoiOption[], item: PoiOption, radius: number): void => {
      if (item.parentCode !== parent.name) {
        const newPOI = createPOIFromOption(item, parent.include, radius, parent.dataProvider);

        setSelectedPOIs((prev) => [...prev, newPOI]);
        return;
      }

      if (siblings.length === 1) {
        setSelectedPOIs((prev) => prev.map((poi) => (matchesParent(poi, parent) ? { ...poi, radius } : poi)));

        return;
      }

      const siblingsToAdd = siblings.map((sibling) => {
        return createPOIFromOption(
          sibling,
          parent.include,
          isSameOption(sibling, item) ? radius : parent.radius,
          parent.dataProvider,
        );
      });

      setSelectedPOIs((prev) => [...removeParent(prev, parent), ...siblingsToAdd]);
    },
    [],
  );

  const onChangeRadius: UsePOI['onChangeRadius'] = useCallback(
    (radius, item, itemIndex) => {
      setCurrentFocusedItemIndex((prev) => (prev !== itemIndex ? itemIndex : prev));

      const existingPOI = getSelectedPOI(item);
      const itemHasChildren = hasChildren(item);
      const isNarrowedParent =
        itemHasChildren &&
        getAllLeafDescendants(item).length <
          getLeafDescendantsUnderAncestor(combinedPoiOptions, getItemPath(item)).length;
      const isNonRootParent = itemHasChildren && Boolean(item.parentCode);

      if (isNarrowedParent || isNonRootParent) {
        handleVisibleLeavesRadiusChange(item, radius, existingPOI);

        return;
      }

      if (existingPOI) {
        handleDirectRadiusChange(existingPOI, radius);

        return;
      }

      const parent = getSelectedParentPOI(item);
      const siblings = getSiblingOptions(combinedPoiOptions, item);

      if (parent && siblings.length > 0) {
        handleInheritedRadiusChange(parent, siblings, item, radius);

        return;
      }

      if (getDescendantsOfItem(item, selectedPOIs).length > 0) {
        handleDescendantsRadiusChange(item, radius);
      }
    },
    [
      getSelectedPOI,
      getSelectedParentPOI,
      handleDirectRadiusChange,
      handleInheritedRadiusChange,
      handleVisibleLeavesRadiusChange,
      handleDescendantsRadiusChange,
      combinedPoiOptions,
      selectedPOIs,
    ],
  );

  const handleConfirm = useCallback((): void => {
    const hasDifferentChildSettings = (parent: POI): boolean =>
      selectedPOIs
        .filter((other) => isLeafChildOfParent(parent, other))
        .some((child) => child.include !== parent.include || child.radius !== parent.radius);

    const expandParentToLeaves = (parent: POI): POI[] => {
      const overrides = selectedPOIs.filter((poi) => isLeafChildOfParent(parent, poi));

      return getLeafDescendantsUnderAncestor(combinedPoiOptions, parent.categoryPath).map((leaf) => {
        const override = overrides.find(
          (overridePoi) =>
            overridePoi.name === leaf.name && overridePoi.categoryPath === (leaf.categoryPath || leaf.categoryType),
        );

        return override ?? createPOIFromOption(leaf, parent.include, parent.radius, parent.dataProvider);
      });
    };

    const parentsToExpand = selectedPOIs.filter((poi) => poi.hasChildren && hasDifferentChildSettings(poi));
    const parentPaths = new Set(parentsToExpand.map((parentPoi) => parentPoi.categoryPath));

    const nonExpanded = selectedPOIs.filter(
      (poi) =>
        !parentPaths.has(poi.categoryPath) && !parentsToExpand.some((parentPoi) => isLeafChildOfParent(parentPoi, poi)),
    );
    const expanded = parentsToExpand.flatMap(expandParentToLeaves);

    enrichedOnConfirm(consolidatePOIs([...nonExpanded, ...expanded], combinedPoiOptions));
  }, [selectedPOIs, combinedPoiOptions, enrichedOnConfirm]);

  const isInsideCustomCategory =
    navigationStack.length > 0 &&
    (navigationStack[0].categoryPath?.startsWith(`${CUSTOM_POI_DATA_PROVIDER}/`) ?? false);

  const resetPOIToPreviousState: UsePOI['resetPOIToPreviousState'] = useCallback(() => setSelectedPOIs(pois), [pois]);

  const resetCurrentFocusedItemIndex = useCallback((): void => setCurrentFocusedItemIndex(null), []);

  useEffect(() => {
    if (!numberOfAllowedPOIs) return;
    setIsNumberOfPOIsExceeded(selectedPOIs.length > numberOfAllowedPOIs);
  }, [selectedPOIs.length, numberOfAllowedPOIs]);

  return {
    getSelectedPOIIncludeAndRadius,
    getSelectedPOIIncludedExcludedChildrenCounter,
    isSelectedPOIChanged,
    isItemSelected,
    onChangeRadius,
    handleConfirm,
    onIncludeExcludeChange,
    resetCurrentFocusedItemIndex,
    resetPOIToPreviousState,
    currentFocusedItemIndex,
    isNumberOfPOIsExceeded,
    isAnyRadiusOfPOIZero,
    customPoiCategories,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isCustomCategory,
    isInsideCustomCategory,
  };
};
